import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { DataService } from "./data.service";
import { Observation } from "../model/observation";
import { AdmissionSearchItem } from "../model/admission-search-item";
import { MedicalOrderOperation } from "../model/medical-order-operation";
import { MedicalOrderScann } from "../model/medical-order-scann";
import { ReportViewer } from "../model/report-viewer";
import { CallerSearchItem } from "../model/caller-search-item";
import { Caller } from "../model/caller";
import { OrientationSearchItem } from "../model/orientation/orientation-search-item";
import { Orientation } from "../model/orientation/orientation";
import { ReApointmentSearchItem } from "../model/re-appointment/re-appointment-search-item";
import { Admission } from "../model/admission";
import { AuthService } from "./auth.service";

@Injectable()
export class AdmissionService {
  constructor(
    private http: HttpClient,
    private dataService: DataService,
    private authService: AuthService
  ) {}


  private readonly getTicketReportPdfEndPoint: string =
    "/admission/getTicketReportPdf/";
  private readonly getQuestionnaireReportPdfEndPoint: string =
    "/admission/getQuestionnaireReportPdf/";
  private readonly getConsentReportPdfEndPoint: string =
    "/admission/getConsentReportPdf/";
  private readonly getOrderReportPdfEndPoint: string =
    "/admission/getOrderReportPdf/";
  private readonly getObservationsEndPoint: string =
    "/admission/GetObservations/";
  private readonly observationsSaveEndPoint: string =
    "/admission/ObservationsSave/";
  private readonly getAdmissionSearchItemsEndPoint: string =
    "/admission/getAdmissionSearchItems/";
  private readonly getAdmissionSearchItemsAdmitedEndPoint: string =
    "/admission/getAdmissionSearchItemsAdmited/";
  private readonly getNewAdmissionEndPoint: string =
    "/admission/getNewAdmission/";
  private readonly getAdmissionEndPoint: string = "/admission/getAdmission/";
  private readonly finishAdmissionEndPoint: string =
    "/admission/finishAdmission/";
  private readonly admissionSaveEndPoint: string = "/admission/admissionSave/";
  private readonly getReAppointmentItemsByParametersEndPoint =
    "/admission/getReAppointmentItemsByParameters";
  private readonly getPatientReceiveAddmissionSearchItemsEndPoint: string =
    "/admission/getPatientReceiveAddmissionSearchItems/";
  private readonly medicalOrderScannUpdateEndPoint: string =
    "/admission/medicalOrderScannUpdate/";
  private readonly medicalOrderScannDeleteEndPoint: string =
    "/admission/medicalOrderScannDelete/";
  private readonly medicalOrderScannUpdateFromTemporaryStorageEndPoint: string =
    "/admission/getMedicalOrderScannUpdateFromTemporaryStorage/";
  private readonly getCallerSearchItemEndPoint: string =
    "/admission/getCallerSearchItem/";
  private readonly getCallerEndPoint: string = "/admission/getCaller/";
  private readonly getOrientationSearchItemEndPoint: string =
    "/admission/getOrientationSearchItem/";
  private readonly getOrientationEndPoint: string =
    "/admission/getOrientation/";
  private readonly getLateAttentionMinutesEndPoint: string =
    "/admission/getLateAttentionMinutes/";
  private readonly setEquipmentUserSectorEndPoint: string =
    "/admission/setEquipmentUserSector/";
  private readonly getIsReAppointmentPendientEndPoint: string =
    "/admission/getIsReAppointmentPendient/";
  private readonly getOrderReportsMergedEndPoint: string =
    "/admission/GetOrderReportsMerged/";
  private readonly changeAppointmentDateEndPoint: string =
    "/admission/changeAppointmentDate/";
  private readonly registerMessagePatientEndPoint: string =
    "/admission/registerMessagePatient/";
  private readonly sendMailOrderReportPdfEndPoint: string =
    "/admission/sendMailOrderReportPdf/";

  async registerMessagePatient(medicalAppointmentId: number): Promise<boolean> {
    const param = { medicalAppointmentId };
    return await this.dataService
      .post<boolean>(this.registerMessagePatientEndPoint, param)
      .toPromise();
  }

  async changeAppointmentDate(
    medicalAppointmentId: number,
    date: Date
  ): Promise<boolean> {
    const param = {
      MedicalAppointmentId: medicalAppointmentId,
      Date: new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      ).toDateString(),
    };
    return await this.dataService
      .post<boolean>(this.changeAppointmentDateEndPoint, param)
      .toPromise();
  }

  async getOrientationSearchItem(name: string): Promise<OrientationSearchItem> {
    const param = { name: name };
    return await this.dataService
      .get<OrientationSearchItem>(this.getOrientationSearchItemEndPoint, param)
      .toPromise();
  }

  async getOrientation(id: number): Promise<Orientation> {
    const param = { id: id };
    return await this.dataService
      .get<Orientation>(this.getOrientationEndPoint, param)
      .toPromise();
  }

  async getCallerSearchItem(name: string): Promise<CallerSearchItem> {
    const param = { name: name };
    return await this.dataService
      .get<CallerSearchItem>(this.getCallerSearchItemEndPoint, param)
      .toPromise();
  }

  async getCaller(id: number): Promise<Caller> {
    const param = { id: id };
    return await this.dataService
      .get<Caller>(this.getCallerEndPoint, param)
      .toPromise();
  }

  async setAdmissionControled(
    medicalApponitmentIds: number[]
  ): Promise<boolean> {
    const request = { MedicalAppointmentIds: medicalApponitmentIds };
    return await this.dataService
      .post<boolean>("/admission/setAdmissionControled/", request)
      .toPromise();
  }

  async rollBackAdmissionControled(
    medicalApponitmentIds: number[]
  ): Promise<boolean> {
    const request = { MedicalAppointmentIds: medicalApponitmentIds };
    return await this.dataService
      .post<boolean>("/admission/rollBackAdmissionControled/", request)
      .toPromise();
  }

  async admissionByMedicalAppointmentIds(
    medicalApponitmentIds: number[]
  ): Promise<number[]> {
    const request = { MedicalAppointmentIds: medicalApponitmentIds };
    return await this.dataService
      .post<number[]>("/admission/admissionByMedicalAppointmentIds/", request)
      .toPromise();
  }

  async getOrderReportPdf(
    medicalApponitmentId: number
  ): Promise<ReportViewer[]> {
    const param = { medicalApponitmentId: medicalApponitmentId };
    return await this.dataService
      .get<ReportViewer[]>(this.getOrderReportPdfEndPoint, param)
      .toPromise();
  }

  async sendMailOrderReportPdf(medicalApponitmentId: number): Promise<boolean> {
    const param = { MedicalApponitmentId: medicalApponitmentId };
    return await this.dataService
      .post<boolean>(this.sendMailOrderReportPdfEndPoint, param)
      .toPromise();
  }

  async getLateAttentionMinutes(medicalApponitmentId: number): Promise<number> {
    const param = { medicalAppointmentId: medicalApponitmentId };
    return await this.dataService
      .get<number>(this.getLateAttentionMinutesEndPoint, param)
      .toPromise();
  }

  async setEquipmentFromUserSector(
    medicalApponitmentId: number
  ): Promise<boolean> {
    const sectorId = this.authService.getSectorId();
    const param = {
      medicalAppointmentId: medicalApponitmentId,
      sectorId: sectorId,
    };
    return await this.dataService
      .post<boolean>(this.setEquipmentUserSectorEndPoint, param)
      .toPromise();
  }



  async getTicketReportPdf(
    medicalApponitmentId: number
  ): Promise<ReportViewer> {
    const param = { medicalApponitmentId: medicalApponitmentId };
    return await this.dataService
      .get<ReportViewer>(this.getTicketReportPdfEndPoint, param)
      .toPromise();
  }

  async getConsentReportPdf(
    medicalApponitmentId: number
  ): Promise<ReportViewer> {
    const param = { medicalApponitmentId: medicalApponitmentId };
    return await this.dataService
      .get<ReportViewer>(this.getConsentReportPdfEndPoint, param)
      .toPromise();
  }

  async getQuestionnaireReportPdf(
    medicalApponitmentId: number
  ): Promise<ReportViewer> {
    const param = { medicalApponitmentId: medicalApponitmentId };
    return await this.dataService
      .get<ReportViewer>(this.getQuestionnaireReportPdfEndPoint, param)
      .toPromise();
  }

  async getPatientReceiveAddmissionSearchItems(
    patientId: number,
    sameDay: boolean
  ): Promise<AdmissionSearchItem[]> {
    const params = { patientId: patientId, sameDay: sameDay };
    return await this.dataService
      .get<AdmissionSearchItem[]>(
        this.getPatientReceiveAddmissionSearchItemsEndPoint,
        params
      )
      .toPromise();
  }

  async getAdmissionSearchItemsAdmited(
    dateFrom: Date,
    dateTo: Date,
    protocol: string,
    companyIds: number[],
    serviceId: number,
    orderByPatient: boolean,
    viewPreAdmission: boolean,
    isIntegrated: boolean,
    viewCancel: boolean,
    patientId?: number,
    practiceRegionId?: number,
    specialityId?: number,
    centerId?: number,
    sectorId?: number,
    physicianId?: number,
    admissionControled?: boolean
  ): Promise<AdmissionSearchItem[]> {
    const params = {
      dateFrom: new Date(
        dateFrom.getFullYear(),
        dateFrom.getMonth(),
        dateFrom.getDate()
      ).toDateString(),
      dateTo: new Date(
        dateTo.getFullYear(),
        dateTo.getMonth(),
        dateTo.getDate()
      ).toDateString(),
      companyIds: JSON.stringify(companyIds),
      isIntegrated,
      protocol: protocol,
      patientId: patientId,
      practiceRegionId: practiceRegionId,
      serviceId: serviceId,
      specialityId: specialityId,
      centerId: centerId,
      sectorId: sectorId,
      physicianId: physicianId,
      orderByPatient,
      viewPreAdmission,
      viewCancel,
      admissionControled,
    };

    return await this.dataService
      .get<AdmissionSearchItem[]>(
        this.getAdmissionSearchItemsAdmitedEndPoint,
        params
      )
      .toPromise();
  }

  async getOrderReportsMerged(
    medicalAppointmentIds: number[]
  ): Promise<ReportViewer> {
    const params = {
      medicalApponitmentIds: JSON.stringify(medicalAppointmentIds),
    };
    return await this.dataService
      .get<ReportViewer>(this.getOrderReportsMergedEndPoint, params)
      .toPromise();
  }

  async getAdmissionSearchItems(
    companyIds: number[],
    serviceIds: number[],
    orderByPatient: boolean,
    date?: Date,
    patientId?: number,
    practiceRegionId?: number,
    specialityId?: number,
    centerId?: number,
    sectorId?: number
  ): Promise<AdmissionSearchItem[]> {
    const params = {
      date: date
        ? new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate()
          ).toDateString()
        : null,
      companyIds: JSON.stringify(companyIds),
      serviceIds: JSON.stringify(serviceIds),
      orderByPatient: orderByPatient,
      patientId: patientId,
      practiceRegionId: practiceRegionId,
      specialityId: specialityId,
      centerId: centerId,
      sectorId: sectorId,
    };
    return await this.dataService
      .get<AdmissionSearchItem[]>(this.getAdmissionSearchItemsEndPoint, params)
      .toPromise();
  }

  async getAdmissionInCombo(medicalAppointmentId: number): Promise<Admission> {
    const params = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<Admission>("/admission/getAdmissionInCombo/", params)
      .toPromise();
  }

  async existsAdmissionInCombo(medicalAppointmentId: number): Promise<boolean> {
    const params = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<boolean>("/admission/existsAdmissionInCombo/", params)
      .toPromise();
  }

  async setAdmissionFinishedByAtention(atention: string): Promise<boolean> {
    const params = { atention };
    return await this.dataService
      .post<boolean>("/admission/admissionFinishedByAtention/", params)
      .toPromise();
  }

  async setAdmissionFinished(medicalAppointmentId: number): Promise<boolean> {
    const params = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .post<boolean>("/admission/admissionFinished/", params)
      .toPromise();
  }

  async getNewAdmission(medicalAppointmentId: number): Promise<Admission> {
    const params = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<Admission>(this.getNewAdmissionEndPoint, params)
      .toPromise();
  }

  async getAdmission(medicalAppointmentId: number): Promise<Admission> {
    const params = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<Admission>(this.getAdmissionEndPoint, params)
      .toPromise();
  }

  async getObservations(medicalAppointmentId: number): Promise<Observation> {
    const params = {
      medicalAppointmentId: medicalAppointmentId,
    };
    return await this.dataService
      .get<Observation>(this.getObservationsEndPoint, params)
      .toPromise();
  }

  async getIsReAppointmentPendient(
    medicalAppointmentId: number
  ): Promise<boolean> {
    const params = {
      medicalAppointmentId: medicalAppointmentId,
    };
    return await this.dataService
      .get<boolean>(this.getIsReAppointmentPendientEndPoint, params)
      .toPromise();
  }

  async observationsSave(
    medicalAppointmentId: number,
    orderObservation: string,
    patientObservation: string,
    auditoriaObservation: string
  ): Promise<boolean> {
    const observation = {
      MedicalAppointmentId: medicalAppointmentId,
      OrderObservation: orderObservation,
      PatientObservation: patientObservation,
      AuditoriaObservation: auditoriaObservation,
    };
    return await this.dataService
      .post<boolean>(this.observationsSaveEndPoint, observation)
      .toPromise();
  }

  async finishAdmission(admission: Admission): Promise<MedicalOrderOperation> {
    return await this.dataService
      .post<MedicalOrderOperation>(this.finishAdmissionEndPoint, admission)
      .toPromise();
  }

  async admissionSave(admission: Admission): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.admissionSaveEndPoint, admission)
      .toPromise();
  }

  async medicalOrderScannUpdate(
    medicalOrderScann: MedicalOrderScann
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.medicalOrderScannUpdateEndPoint, medicalOrderScann)
      .toPromise();
  }

  async medicalOrderScannDelete(
    medicalOrderScann: MedicalOrderScann
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.medicalOrderScannDeleteEndPoint, medicalOrderScann)
      .toPromise();
  }

  async medicalOrderScannUpdateFromTemporaryStorage(
    imgId: number
  ): Promise<MedicalOrderScann> {
    const params = { imgId: imgId };
    return await this.dataService
      .get<MedicalOrderScann>(
        this.medicalOrderScannUpdateFromTemporaryStorageEndPoint,
        params
      )
      .toPromise();
  }

  async getReAppointmentItemsByParameters(
    from: Date,
    to: Date,
    serviceIds: number[],
    centerIds: number[],
    viewCancel: boolean,
    reApointmentPendiet: boolean,
    reAppointment: boolean,
    patienteId?: number
  ): Promise<ReApointmentSearchItem[]> {
    const params = {
      from: new Date(
        from.getFullYear(),
        from.getMonth(),
        from.getDate()
      ).toDateString(),
      to: new Date(
        to.getFullYear(),
        to.getMonth(),
        to.getDate()
      ).toDateString(),
      patienteId: patienteId,
      serviceIds: JSON.stringify(serviceIds),
      centerIds: JSON.stringify(centerIds),
      viewCancel: viewCancel,
      reApointmentPendiet: reApointmentPendiet,
      reAppointment: reAppointment,
    };

    return await this.dataService
      .get<ReApointmentSearchItem[]>(
        this.getReAppointmentItemsByParametersEndPoint,
        params
      )
      .toPromise();
  }
}
